// src/components/dashboard/VehicleFleetPage.jsx
import React, { useState, useEffect } from 'react';
import { Truck, Container, Car, Plus, Link, Download, Edit, Trash2, Search, Check, AlertCircle, FileText, X } from 'lucide-react';
import AddVehicleForm from './AddVehicleForm';
import { api } from '../../services/api'; // Import the API
import { useLanguage } from '../../contexts/LanguageContext';

const Notification = ({ message, type, onClose }) => {
  if (!message) return null;
  
  return (
    <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md max-w-md z-50 flex items-center justify-between ${
      type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 
      type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' : 
      'bg-blue-100 text-blue-800 border border-blue-200'
    }`}>
      <div className="flex items-center">
        {type === 'success' && <Check className="h-5 w-5 mr-2" />}
        {type === 'error' && <AlertCircle className="h-5 w-5 mr-2" />}
        {type === 'info' && <FileText className="h-5 w-5 mr-2" />}
        <p>{message}</p>
      </div>
      <button onClick={onClose} className="ml-4 text-gray-500 hover:text-gray-700">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export const VehicleFleetPage = () => {
  const [isAddVehicleFormOpen, setIsAddVehicleFormOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language, setLanguage, t } = useLanguage();
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [notification, setNotification] = useState({ message: '', type: '' });

  // Fetch vehicles on component mount
  useEffect(() => {
    fetchVehicles();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: '', type: '' }), 5000);
  };

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const data = await api.getVehicles();
      setVehicles(data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      showNotification('Failed to load vehicles. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get what a vehicle is hitched to OR what is hitched to it
  const getHitchDisplay = (vehicle) => {
    // If this vehicle has hitch_to (tractor/truck hitched to semi-trailer)
    if (vehicle.hitch_to) {
      return vehicle.hitch_to;
    }
    
    // If this is a semi-trailer, check if any tractor/truck is hitched to it
    if (vehicle.type === 'semi_trailer') {
      const hitchedVehicle = vehicles.find(v => v.hitch_to === vehicle.plate_number);
      if (hitchedVehicle) {
        return hitchedVehicle.plate_number;
      }
    }
    
    return '-';
  };

  const handleOpenAddVehicleForm = () => {
    setEditingVehicle(null);
    setIsAddVehicleFormOpen(true);
  };

  const handleCloseAddVehicleForm = () => {
    setIsAddVehicleFormOpen(false);
    setEditingVehicle(null);
  };

  const handleAddVehicle = async (vehicleData) => {
    try {
      if (editingVehicle) {
        // Update existing vehicle
        const updatedVehicle = await api.updateVehicle(editingVehicle.id, vehicleData);
        setVehicles(prev => prev.map(v => v.id === editingVehicle.id ? updatedVehicle : v));
        showNotification(t('registrationUpdated'), 'success');
      } else {
        // Add new vehicle
        const newVehicle = await api.addVehicle(vehicleData);
        
        // Make sure the new vehicle has all required fields including id
        if (newVehicle && newVehicle.id) {
          setVehicles(prev => [...prev, newVehicle]);
          showNotification('Vehicle registered successfully!', 'success');
        } else {
          console.error('Invalid vehicle data received:', newVehicle);
          // Refresh the list from server to ensure we have correct data
          await fetchVehicles();
          showNotification('Vehicle registered successfully!', 'success');
        }
      }
      handleCloseAddVehicleForm();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      // Re-throw so AddVehicleForm surfaces the error inline (red field + reason),
      // consistent with the admin form — no toast popup.
      throw error;
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm(t('confirmDelete'))) {
      return;
    }

    try {
      await api.deleteVehicle(vehicleId);
      setVehicles(prev => prev.filter(vehicle => vehicle.id !== vehicleId));
      showNotification(t('registrationDeleted'), 'success');
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      showNotification('Error deleting vehicle. Please try again.', 'error');
    }
  };

  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setIsAddVehicleFormOpen(true);
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.trim() === '') {
      fetchVehicles();
      return;
    }

    try {
      const results = await api.searchVehicles(query);
      setVehicles(results);
    } catch (error) {
      console.error('Error searching vehicles:', error);
    }
  };

  const handleExportToExcel = () => {
    if (vehicles.length === 0) {
      showNotification('No vehicles to export', 'info');
      return;
    }

    // Create HTML table for Excel
    let tableHTML = '<table border="1">';
    
    // Add headers
    tableHTML += '<thead><tr>';
    const headers = [
      'N', 'Type', 'Plate Number', 'VIN', 'Passport Number', 'Year', 
      'Make', 'Model', 'Driver Name', 'Driver Passport', 'Hitch To', 
      'Net Weight (kg)', 'Max Load (kg)'
    ];
    headers.forEach(header => {
      tableHTML += `<th>${header}</th>`;
    });
    tableHTML += '</tr></thead>';
    
    // Add data rows
    tableHTML += '<tbody>';
    vehicles.forEach((vehicle, index) => {
      tableHTML += '<tr>';
      tableHTML += `<td>${index + 1}</td>`;
      tableHTML += `<td>${vehicle.type.replace('_', ' ')}</td>`;
      tableHTML += `<td>${vehicle.plate_number}</td>`;
      tableHTML += `<td>${vehicle.vin || '-'}</td>`;
      tableHTML += `<td>${vehicle.passport_number || '-'}</td>`;
      tableHTML += `<td>${vehicle.vehicle_year || '-'}</td>`;
      tableHTML += `<td>${vehicle.make || '-'}</td>`;
      tableHTML += `<td>${vehicle.model || '-'}</td>`;
      tableHTML += `<td>${vehicle.driver_name || '-'}</td>`;
      tableHTML += `<td>${vehicle.driver_passport_number || '-'}</td>`;
	  tableHTML += `<td>${vehicle.driver_phonenumber || '-'}</td>`;
      tableHTML += `<td>${getHitchDisplay(vehicle)}</td>`;
      tableHTML += `<td>${vehicle.net_weight || 0}</td>`;
      tableHTML += `<td>${vehicle.max_load || '-'}</td>`;
      tableHTML += '</tr>';
    });
    tableHTML += '</tbody></table>';

    // Create blob with proper Excel format
    const blob = new Blob([tableHTML], { 
      type: 'application/vnd.ms-excel' 
    });
    
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `vehicle_fleet_${new Date().toISOString().split('T')[0]}.xls`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  return (
    <div className="container mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-6">
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-6 sm:mb-8">
        <button 
          onClick={handleOpenAddVehicleForm}
          className="px-2 sm:px-3 py-1.5 sm:py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center text-xs sm:text-sm"
        >
          <Plus className="h-4 w-4" />
          {t('regVehicle')}
        </button>
        <button 
          onClick={handleExportToExcel}
          className="px-2 sm:px-3 py-1.5 sm:py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center text-xs sm:text-sm"
        >
          <Download className="h-4 w-4" />
          {t('exportExcel')}
        </button>
      </div>

      {/* Add/Edit Vehicle Form Modal */}
      <AddVehicleForm
        isOpen={isAddVehicleFormOpen}
        onClose={handleCloseAddVehicleForm}
        onSubmit={handleAddVehicle}
        initialData={editingVehicle}
        allVehicles={vehicles}
      />

      {/* Vehicle Fleet Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 sm:mb-8">
        {/* Tractors Card */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">{t('tractor')}</h3>
              <p className="text-2xl font-bold text-gray-900">
                {vehicles.filter(v => v.type === 'tractor').length}
              </p>
            </div>
            <Truck className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        {/* Semi-Trailers Card */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">{t('trailer')}</h3>
              <p className="text-2xl font-bold text-gray-900">
                {vehicles.filter(v => v.type === 'semi_trailer').length}
              </p>
            </div>
            <Container className="h-8 w-8 text-green-600" />
          </div>
        </div>

        {/* Trucks Card */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">{t('truck')}</h3>
              <p className="text-2xl font-bold text-gray-900">
                {vehicles.filter(v => v.type === 'truck').length}
              </p>
            </div>
            <Truck className="h-8 w-8 text-orange-600" />
          </div>
        </div>

        {/* Others Card */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">{t('other')}</h3>
              <p className="text-2xl font-bold text-gray-900">
                {vehicles.filter(v => v.type === 'other').length}
              </p>
            </div>
            <Car className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Search Field */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={t('search')}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading vehicles...</p>
        </div>
      ) : (
        /* Vehicles Table */
        <div className="bg-white rounded-lg shadow overflow-hidden w-full">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('vehicleType')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('licensePlate')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VIN</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('vehiclePassport')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('manufacturer')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('model')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('driverInfo')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('driverPass')}</th>
				  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('phone')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hitched')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('maxLoad')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vehicles.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="px-6 py-8 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <Truck className="h-12 w-12 text-gray-400 mb-2" />
                        <p className="text-lg font-medium text-gray-900 mb-1">No vehicles registered</p>
                        <p className="text-sm text-gray-500">Get started by registering your first vehicle</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  vehicles.map((vehicle, index) => (
                    <tr key={vehicle.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                        {vehicle.type.replace('_', ' ')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {vehicle.plate_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vehicle.vin || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vehicle.passport_number || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vehicle.make || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vehicle.model || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vehicle.driver_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vehicle.driver_passport_number || '-'}
                      </td>
					  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vehicle.driver_phonenumber || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getHitchDisplay(vehicle)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vehicle.max_load ? `${vehicle.max_load} kg` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button 
                            onClick={() => handleEditVehicle(vehicle)}
                            className="text-blue-600 hover:text-blue-900"
                            title={t('edit')}
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteVehicle(vehicle.id)}
                            className="text-red-600 hover:text-red-900"
                            title={t('delete')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      <Notification 
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: '', type: '' })}
      />
    </div>
  );
};

export default VehicleFleetPage;